<script setup lang="ts">
/**
 * ニックネーム投稿コンポーネント。
 * 入力されたニックネームは、Store層(+LocalStorage)へ保持される。
 * バリデーションエラー表示にも対応。
 */

import { ApiValidationErrorViewModel } from "@/viewModels/apiErrorViewModel";
import { PropType, reactive, toRefs, inject, defineProps } from "vue";
import { CommunityTalkStore, isValidCommunityTalkStoreKey } from "@/store/communityTalk/communityTalkStore";
import AppConst from "@/modules/const";
import CustomInput from "../common/form/CustomInput.vue";
import CustomLabel from "./CustomLabel.vue";

const props = defineProps({
	/**
	 * CommunityTalkStoreのInject用キー。
	 */
	communityTalkStoreKey: {
		type: String,
		required: true,
		default: "",
	},
	/**
	 * 投稿種類。
	 * トークルームとしてのニックネーム: `TalkRoom`
	 * リプライとしてのニックネーム: `Reply`
	 */
	postType: {
		type: String,
		required: true,
		default: "TalkRoom",
		validator(val: string) {
			return ["TalkRoom", "Reply"].includes(val);
		},
	},
	/**
	 * 入力欄を無効化するかどうか。
	 */
	disabled: {
		type: Boolean,
		required: false,
		default: false,
	},
	/**
	 * 親コンポーネントからのAPIエラーオブジェクト。
	 */
	apiValidationErrorViewModel: {
		type: Object as PropType<ApiValidationErrorViewModel | null>,
		required: true,
		default: null,
	},
	/**
	 * 入力必須エラーを表示するかどうか。
	 */
	showRequiredError: {
		type: Boolean,
		required: false,
		default: false,
	},
});

// Storeの初期化処理
if (!isValidCommunityTalkStoreKey(props.communityTalkStoreKey)) {
	// なんらかの原因でInjectKeyにエラーが発生していた場合は強制終了
	throw new Error(`InjectKeyにエラーが発生しました。${props.communityTalkStoreKey.toString()}`);
}
const communityTalkStore = inject(props.communityTalkStoreKey) as CommunityTalkStore;

interface State {
	/**
	 * 入力ニックネームの内容。
	 */
	inputNickName: string;
}
// reactiveな変数宣言
const state = reactive<State>({
	inputNickName: communityTalkStore.getCommunityTalkUserNickName(),
});
const { inputNickName } = toRefs(state);

const name = props.postType === "TalkRoom" ? "nickName" : "reply.NickName";

// 入力欄変更時、Storeへ投稿者プロフィール情報を反映
const onChange = (): void => {
	communityTalkStore.setCommunityTalkUserNickName(state.inputNickName);
};

/**
 * ニックネームの投稿最大文字長。
 */
const nickNameMaxLength = AppConst.COMMUNITY_TALK_USER_NICKNAME_MAX_LENGTH;

/**
 * ニックネームが必須かどうか。
 */
const isRequiredNickname = communityTalkStore.getGeneralSettings().nicknameRequiredSetting === "Required";
</script>

<template>
	<div class="revico-community-talk-post--nickname">
		<custom-label :text="`ニックネーム ${isRequiredNickname ? '(必須)' : ''}`" :disabled="disabled" />
		<custom-input
			v-model="inputNickName"
			@update:modelValue="onChange()"
			:name="name"
			type="text"
			customClass="revico-community-talk-post--nickname-input"
			:api-validation-error-view-model="showRequiredError ? null : apiValidationErrorViewModel"
			:maxlength="nickNameMaxLength"
			:disabled="disabled"
		/>
		<span v-if="showRequiredError" class="revico-community-talk-post--nickname-error-message">ニックネームを入力してください。</span>
	</div>
</template>

<style lang="scss">
.revico-widget .revico-community-talk .revico-community-talk {
	&-post--nickname {
		margin-top: 16px;
		&-input {
			@include communitytalk_input(#fff);
			@include disabledInput;
		}
		&-error-message {
			display: inline-block;
			margin-top: 4px;
			color: var(--base-error-message-font-color);
			font-size: 0.9em;
		}
	}
}
</style>
